import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { visitAPI, partyAPI, requestAPI, clinicalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const fmtErr = (d, fb) => { if (!d) return fb; if (typeof d === 'string') return d; if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; '); return d.msg || d.message || JSON.stringify(d); };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

const STATUSES = ['SCHEDULED', 'NURSE_EN_ROUTE', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const FILTER_DEF = [
  { key: 'status',    label: 'Status',     type: 'select', options: STATUSES },
  { key: 'date_from', label: 'From Date',  type: 'date' },
  { key: 'date_to',   label: 'To Date',    type: 'date' },
];
const BLANK_F = { status: '', date_from: '', date_to: '' };

// Status color map for the summary bar
const STATUS_COLOR = {
  SCHEDULED:     '#f6d6e6',
  NURSE_EN_ROUTE:'#fef9c3',
  IN_PROGRESS:   '#d1fae5',
  COMPLETED:     '#dcfce7',
  CANCELLED:     '#fee2e2',
};

export default function Visits() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const isDoctor = roles.includes('DOCTOR');
  const isNurse  = !isDoctor && roles.includes('NURSE');
  // Doctors/nurses ARE the visiting clinician — no picker for them.
  const selfClinician = isDoctor || isNurse;
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const PER = 50;
  const [filters, setFilters] = useState(BLANK_F);

  const [patients, setPatients] = useState([]);
  const [nurses, setNurses]     = useState([]);

  // Create — ?create=1&request=&patient=&pname= prefills from a care request
  // (the doctor "Visit" action): patient is fixed, clinician is the caller.
  const [params, setParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(params.get('create') === '1');
  const [cf, setCf] = useState({
    request_id: params.get('request') || '',
    patient_id: params.get('patient') || '',
    patient_name: params.get('pname') || '',
    nurse_id: '', scheduled_start: '', scheduled_end: '',
  });
  const [cErr, setCErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit status
  const [editRow, setEditRow] = useState(null);
  const [ef, setEf]           = useState({ visit_status: 'SCHEDULED', code: '' });
  const [eErr, setEErr]       = useState('');
  const [eSaving, setESaving] = useState(false);

  // Visit report — nurse documents the visit (M6); everyone views it
  const [reportRow, setReportRow] = useState(null);   // create-form target
  const [rf, setRf] = useState({});
  const [rErr, setRErr] = useState('');
  const [rSaving, setRSaving] = useState(false);
  const [viewReport, setViewReport] = useState(null); // {visit, report}

  const openReportForm = (row) => { setReportRow(row); setRf({ patient_condition: 'STABLE' }); setRErr(''); };
  const submitReport = async e => {
    e.preventDefault(); setRSaving(true); setRErr('');
    try {
      const num = v => (v === '' || v == null ? undefined : Number(v));
      await clinicalAPI.submitReport({
        visit_id: reportRow.visit_id,
        blood_pressure_sys: num(rf.bp_sys), blood_pressure_dia: num(rf.bp_dia),
        heart_rate: num(rf.heart_rate), temperature: num(rf.temperature),
        oxygen_saturation: num(rf.spo2), blood_sugar: num(rf.blood_sugar),
        weight_kg: num(rf.weight_kg),
        observations: rf.observations || undefined,
        patient_condition: rf.patient_condition || undefined,
        medications_administered: rf.medications ? rf.medications.split(',').map(x => x.trim()).filter(Boolean) : undefined,
        procedures_performed: rf.procedures ? rf.procedures.split(',').map(x => x.trim()).filter(Boolean) : undefined,
        next_visit_required: !!rf.next_visit_required,
        recommended_next_date: rf.next_date || undefined,
        notes: rf.notes || undefined,
      });
      setReportRow(null); load();
    } catch (err) { setRErr(fmtErr(err.response?.data?.detail, 'Failed to submit report')); }
    finally { setRSaving(false); }
  };
  const openReportView = async (row) => {
    try {
      const r = await clinicalAPI.getVisitReport(row.visit_id);
      setViewReport({ visit: row, report: r?.data ?? r });
    } catch { /* no report yet */ }
  };

  // Cancel
  const [cancelRow, setCancelRow]   = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancel]  = useState(false);

  const buildParams = useCallback(() => {
    const p = { page, per_page: PER };
    if (filters.status)    p.status     = filters.status;
    if (filters.date_from) p.date_from  = filters.date_from;
    if (filters.date_to)   p.date_to    = filters.date_to;
    return p;
  }, [filters, page]);

  const load = useCallback(({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    visitAPI.list(buildParams())
      .then(r => { setRows(r.data || []); setTotal(r.meta?.total || 0); })
      .catch(() => { if (!silent) { setRows([]); setTotal(0); } })
      .finally(() => { if (!silent) setLoading(false); });
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);
  // Live list — visit status changes from the nurse app appear without reload.
  useAutoRefresh(useCallback(() => load({ silent: true }), [load]), 15000);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => {
    if (isPatientOnly) return; // party lists are staff-only; patient view is read-only
    partyAPI.list({ role: 'PATIENT', per_page: 200 }).then(r => setPatients(r.data || []));
    partyAPI.list({ role: 'NURSE',   per_page: 200 }).then(r => setNurses(r.data || []));
  }, [isPatientOnly]);

  const handleCreate = async e => {
    e.preventDefault(); setSaving(true); setCErr('');
    try {
      const start = cf.scheduled_start;
      const end = cf.scheduled_end ||
        (start ? new Date(new Date(start).getTime() + 60 * 60 * 1000).toISOString().slice(0, 16) : undefined);
      await visitAPI.create({
        request_id:      cf.request_id || undefined,
        patient_id:      cf.patient_id,
        // Omitted for doctors/nurses — the backend uses the caller.
        nurse_id:        selfClinician ? undefined : (cf.nurse_id || undefined),
        visit_type:      isDoctor ? 'DOCTOR_ASSESSMENT' : 'NURSE_VISIT',
        scheduled_start: start || undefined,
        scheduled_end:   end,
      });
      setShowCreate(false);
      setParams({}, { replace: true });
      setCf({ request_id: '', patient_id: '', patient_name: '', nurse_id: '', scheduled_start: '', scheduled_end: '' });
      load();
    } catch (err) { setCErr(fmtErr(err.response?.data?.detail, 'Failed to schedule visit')); }
    finally { setSaving(false); }
  };

  const openEdit = row => { setEditRow(row); setEf({ visit_status: row.visit_status, code: '' }); setEErr(''); };
  const handleEdit = async e => {
    e.preventDefault(); setESaving(true); setEErr('');
    try {
      const body = { visit_status: ef.visit_status, code: ef.code || undefined };
      // Check-in captures the nurse's GPS (anti-fraud verification).
      if (ef.visit_status === 'IN_PROGRESS') {
        try {
          const { getPosition } = await import('../../utils/geo');
          const { lat, lng } = await getPosition();
          body.checkin_lat = lat; body.checkin_lng = lng;
        } catch { /* GPS optional - codes are the hard control */ }
      }
      await visitAPI.updateStatus(editRow.visit_id, body);
      setEditRow(null); load();
    } catch (err) { setEErr(fmtErr(err.response?.data?.detail, 'Failed to update status')); }
    finally { setESaving(false); }
  };

  const handleCancel = async () => {
    setCancel(true);
    try {
      await visitAPI.cancel(cancelRow.visit_id, cancelReason || undefined);
      setCancelRow(null); setCancelReason(''); load();
    } catch {} finally { setCancel(false); }
  };

  const columns = [
    { key: 'scheduled_start', label: 'Scheduled',  render: v => v ? new Date(v).toLocaleString() : '—' },
    { key: 'patient_id',      label: 'Patient',    render: (_, r) => isPatientOnly ? (user?.name || 'You') : (r.patient_name || r.patient_id?.slice(0, 8) || '—') },
    { key: 'nurse_id',        label: 'Clinician',  render: (_, r) => r.clinician_name || r.nurse_name || r.nurse_id?.slice(0, 8) || '—' },
    { key: 'visit_type',      label: 'Type',       render: v => v === 'DOCTOR_ASSESSMENT'
        ? <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#5b21b6' }}>Doctor assessment</span>
        : <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#ecfeff', color: '#155e75' }}>Nurse visit</span> },
    ...(isPatientOnly ? [{
      key: 'checkin_code', label: 'Visit codes', render: (_, r) => (r.checkin_code ? (
        <span style={{ fontSize: 11.5, lineHeight: 1.7 }}>
          <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 6, padding: '2px 8px', fontWeight: 800, fontFamily: 'ui-monospace, Menlo, monospace' }}>▶ {r.checkin_code}</span>{' '}
          <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '2px 8px', fontWeight: 800, fontFamily: 'ui-monospace, Menlo, monospace' }}>■ {r.checkout_code}</span>
          <span style={{ display: 'block', fontSize: 10, color: '#a99aa0' }}>give ▶ to start, ■ to end</span>
        </span>
      ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>),
    }] : []),
    { key: 'patient_confirmed', label: 'Confirmed', render: (v, r) => v
        ? <span style={{ color: '#15803d', fontWeight: 700, fontSize: 12 }}>✓ Yes</span>
        : (isPatientOnly && ['SCHEDULED', 'RESCHEDULED'].includes(r.visit_status)
            ? <button onClick={() => visitAPI.confirm(r.visit_id).then(load)} style={btnSm('#15803d')}>Confirm</button>
            : <span style={{ color: '#a99aa0', fontSize: 12 }}>Pending</span>) },
    { key: 'scheduled_end',   label: 'End',        render: v => v ? new Date(v).toLocaleTimeString() : '—' },
    { key: 'checkin_time',    label: 'Check-in',   render: v => v ? new Date(v).toLocaleTimeString() : '—' },
    { key: 'checkout_time',   label: 'Check-out',  render: v => v ? new Date(v).toLocaleTimeString() : '—' },
    { key: 'visit_status',    label: 'Status',     render: v => <Badge value={v} /> },
    {
      key: '_actions', label: 'Actions', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {!isPatientOnly && !row.has_report && ['IN_PROGRESS', 'COMPLETED'].includes(row.visit_status) && (
            <button title="Document this visit (vitals, meds, condition)"
              onClick={() => openReportForm(row)} style={btnSm('#8b5cf6')}>📝 Report</button>
          )}
          {row.has_report && (
            <button title="View the clinical report"
              onClick={() => openReportView(row)} style={btnSm('#8b7681')}>👁 Report</button>
          )}
          {!isPatientOnly && <button onClick={() => openEdit(row)} style={btnSm('#94335a')} disabled={['CANCELLED', 'COMPLETED'].includes(row.visit_status)}>✏️</button>}
          {!isPatientOnly && <button onClick={() => { setCancelRow(row); setCancelReason(''); }} style={btnSm('#ef4444')} disabled={['CANCELLED', 'COMPLETED'].includes(row.visit_status)}>✕</button>}
        </div>
      ),
    },
  ];

  // Status summary counts
  const counts = STATUSES.reduce((acc, s) => { acc[s] = rows.filter(r => r.visit_status === s).length; return acc; }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>{isPatientOnly ? 'My Visits' : 'Visit Schedule'}</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>{isPatientOnly ? 'Your scheduled and past home-care visits.' : `${total} visits · showing ${rows.length}`}</p>
        </div>
        {!isPatientOnly && <button onClick={() => setShowCreate(true)} style={btnPrimary('#10b981')}>+ Schedule Visit</button>}
      </div>

      {/* Status summary bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <div key={s} style={{ background: STATUS_COLOR[s] || '#f1f5f9', borderRadius: 8, padding: '8px 14px', textAlign: 'center', minWidth: 90 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#2b1b22' }}>{counts[s]}</div>
            <div style={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>{s.replace(/_/g, ' ')}</div>
          </div>
        ))}
      </div>

      <FilterBar filters={FILTER_DEF} values={filters} onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))} onClear={() => setFilters(BLANK_F)} />

      {loading ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div> : <Table columns={columns} data={rows} />}
      <Pagination page={page} total={total} per={PER} onPage={setPage} />

      {/* ── Visit Report — create (nurse documentation, M6) ── */}
      {reportRow && (
        <Modal title="Visit Report" onClose={() => setReportRow(null)} fullscreen>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
          {rErr && <ErrBox msg={rErr} />}
          <p style={{ fontSize: 13, color: '#8b7681', margin: '0 0 16px' }}>
            Clinical documentation for {reportRow.patient_name || 'the patient'} — vitals are LOINC-coded,
            and the report becomes part of the auditable record.
          </p>
          <form onSubmit={submitReport}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[['bp_sys', 'BP systolic (mmHg)'], ['bp_dia', 'BP diastolic'], ['heart_rate', 'Heart rate (/min)'],
                ['temperature', 'Temp (°C)'], ['spo2', 'SpO₂ (%)'], ['blood_sugar', 'Glucose (mmol/L)'],
                ['weight_kg', 'Weight (kg)']].map(([k, label]) => (
                <div key={k}>
                  <label style={lbl}>{label}</label>
                  <input style={inp} type="number" step="any" value={rf[k] || ''}
                    onChange={e => setRf(f => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label style={lbl}>Patient condition</label>
                <select style={inp} value={rf.patient_condition || 'STABLE'}
                  onChange={e => setRf(f => ({ ...f, patient_condition: e.target.value }))}>
                  {['STABLE', 'IMPROVING', 'DETERIORATING', 'CRITICAL'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
              <div>
                <label style={lbl}>Medications administered (comma-separated)</label>
                <input style={inp} value={rf.medications || ''} placeholder="e.g. Amoxicillin 500mg, Paracetamol 1g"
                  onChange={e => setRf(f => ({ ...f, medications: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Procedures performed (comma-separated)</label>
                <input style={inp} value={rf.procedures || ''} placeholder="e.g. Wound dressing change"
                  onChange={e => setRf(f => ({ ...f, procedures: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Clinical observations</label>
              <textarea style={{ ...inp, minHeight: 90 }} value={rf.observations || ''}
                onChange={e => setRf(f => ({ ...f, observations: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                <input type="checkbox" checked={!!rf.next_visit_required}
                  onChange={e => setRf(f => ({ ...f, next_visit_required: e.target.checked }))} />
                Follow-up visit required
              </label>
              {rf.next_visit_required && (
                <input style={{ ...inp, width: 180 }} type="date" value={rf.next_date || ''}
                  onChange={e => setRf(f => ({ ...f, next_date: e.target.value }))} />
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, minHeight: 60 }} value={rf.notes || ''}
                onChange={e => setRf(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <FmtActions saving={rSaving} onCancel={() => setReportRow(null)} label="Submit Report" color="#8b5cf6" />
          </form>
          </div>
        </Modal>
      )}

      {/* ── Visit Report — view + print ── */}
      {viewReport && (
        <Modal title="Clinical Report" onClose={() => setViewReport(null)} fullscreen>
          <div id="hhms-print-report" style={{ maxWidth: 760, margin: '0 auto', fontSize: 13.5, color: '#2b1b22' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Benevita — Visit Report</div>
                <div style={{ color: '#8b7681', fontSize: 12 }}>
                  {viewReport.visit.patient_name} · {new Date(viewReport.visit.scheduled_start).toLocaleString()}
                  · Clinician: {viewReport.visit.clinician_name || '—'}
                </div>
              </div>
              <button onClick={() => window.print()} style={btnSm('#94335a')} className="hhms-no-print">🖨 Print</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              {Object.entries({
                'Blood pressure': viewReport.report.vitals?.blood_pressure,
                'Heart rate': viewReport.report.vitals?.heart_rate && `${viewReport.report.vitals.heart_rate} /min`,
                'Temperature': viewReport.report.vitals?.temperature && `${viewReport.report.vitals.temperature} °C`,
                'SpO₂': viewReport.report.vitals?.oxygen_saturation && `${viewReport.report.vitals.oxygen_saturation} %`,
                'Glucose': viewReport.report.vitals?.blood_sugar && `${viewReport.report.vitals.blood_sugar} mmol/L`,
                'Weight': viewReport.report.vitals?.weight_kg && `${viewReport.report.vitals.weight_kg} kg`,
              }).map(([k, v]) => (
                <div key={k} style={{ background: '#f8fafc', border: '1px solid #ece3e7', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 10.5, color: '#8b7681', fontWeight: 700, textTransform: 'uppercase' }}>{k}</div>
                  <div style={{ fontWeight: 700 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            {[['Condition', viewReport.report.patient_condition],
              ['Medications administered', (viewReport.report.medications_administered || []).join(', ')],
              ['Procedures performed', (viewReport.report.procedures_performed || []).join(', ')],
              ['Observations', viewReport.report.observations],
              ['Follow-up', viewReport.report.next_visit_required
                ? `Required${viewReport.report.recommended_next_date ? ' by ' + viewReport.report.recommended_next_date : ''}` : 'Not required'],
              ['Notes', viewReport.report.notes],
            ].map(([k, v]) => v ? (
              <div key={k} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#8b7681', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k}</div>
                <div style={{ marginTop: 3, lineHeight: 1.6 }}>{v}</div>
              </div>
            ) : null)}
            <div style={{ marginTop: 20, paddingTop: 10, borderTop: '1px solid #ece3e7', fontSize: 11, color: '#a99aa0' }}>
              Submitted {viewReport.report.created_at ? new Date(viewReport.report.created_at).toLocaleString() : '—'} ·
              Report ID {String(viewReport.report.report_id || '').slice(0, 8)} · PHI — handle per HIPAA minimum necessary.
            </div>
          </div>
          <style>{`@media print { body * { visibility: hidden; } #hhms-print-report, #hhms-print-report * { visibility: visible; } #hhms-print-report { position: absolute; left: 0; top: 0; width: 100%; } .hhms-no-print { display: none; } }`}</style>
        </Modal>
      )}

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="Schedule Visit" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {cErr && <ErrBox msg={cErr} />}
          <form onSubmit={handleCreate}>
            <Grid2>
              <div>
                <label style={lbl}>Patient *</label>
                {cf.request_id && cf.patient_id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fdeef5', border: '1px solid #f0c8da', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#7b1e3c' }}>
                    {cf.patient_name || (patients.find(p => p.party_id === cf.patient_id)?.name) || cf.patient_id.slice(0, 8)} · from request
                  </span>
                ) : (
                  <select style={inp} value={cf.patient_id} onChange={e => setCf(f => ({ ...f, patient_id: e.target.value }))} required>
                    <option value="">— Select patient —</option>
                    {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label style={lbl}>{isDoctor ? 'Doctor' : 'Clinician'} *</label>
                {selfClinician ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#2b1b22' }}>
                    {user?.name} (you)
                  </span>
                ) : (
                  <select style={inp} value={cf.nurse_id} onChange={e => setCf(f => ({ ...f, nurse_id: e.target.value }))} required>
                    <option value="">— Select nurse —</option>
                    {nurses.map(n => <option key={n.party_id} value={n.party_id}>{n.name}</option>)}
                  </select>
                )}
              </div>
            </Grid2>
            {isDoctor && cf.request_id && (
              <div style={{ marginTop: 10, padding: '9px 13px', borderRadius: 8, fontSize: 12.5, background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#5b21b6' }}>
                This schedules a <strong>doctor assessment</strong> — the patient will confirm availability,
                and after the visit you create the care plan, which auto-dispatches the nurse.
              </div>
            )}
            <Grid3 style={{ marginTop: 14 }}>
              <F label="Start Time *" k="scheduled_start" type="datetime-local" form={cf} set={setCf} required />
              <F label="End Time"     k="scheduled_end"   type="datetime-local" form={cf} set={setCf} />
              <F label="Request ID (optional)" k="request_id" form={cf} set={setCf} placeholder="Linked care request UUID" />
            </Grid3>
            <FmtActions saving={saving} onCancel={() => setShowCreate(false)} label="Schedule Visit" color="#10b981" />
          </form>
          </div>
        </Modal>
      )}

      {/* ── Edit Status Modal ── */}
      {editRow && (
        <Modal title={`Update Visit Status`} onClose={() => setEditRow(null)}>
          {eErr && <ErrBox msg={eErr} />}
          <p style={{ fontSize: 13, color: '#8b7681', margin: '0 0 14px' }}>
            Visit scheduled {editRow.scheduled_start ? new Date(editRow.scheduled_start).toLocaleString() : '—'}
          </p>
          <form onSubmit={handleEdit}>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Visit Status</label>
              <select style={inp} value={ef.visit_status} onChange={e => setEf(f => ({ ...f, visit_status: e.target.value }))}>
                {STATUSES.filter(s => s !== 'CANCELLED').map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            {editRow.visit_type !== 'DOCTOR_ASSESSMENT' && ['IN_PROGRESS', 'COMPLETED'].includes(ef.visit_status) && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>
                  {ef.visit_status === 'IN_PROGRESS' ? '🔑 Start code (ask the patient)' : '🔑 End code (ask the patient)'}
                </label>
                <input style={{ ...inp, letterSpacing: 6, fontWeight: 700, fontSize: 16, textAlign: 'center' }}
                  maxLength={4} inputMode="numeric" placeholder="• • • •"
                  value={ef.code} onChange={e => setEf(f => ({ ...f, code: e.target.value.replace(/\D/g, '') }))} />
                <div style={{ fontSize: 11.5, color: '#8b7681', marginTop: 5, lineHeight: 1.5 }}>
                  {ef.visit_status === 'IN_PROGRESS'
                    ? 'The patient has a 4-digit start code on their My Visits page. Your GPS is captured at check-in. Verified time drives the bill.'
                    : 'The patient gives you the end code when care is complete - this closes the visit and the billable time.'}
                </div>
              </div>
            )}
            <FmtActions saving={eSaving} onCancel={() => setEditRow(null)} label="Update Status" color="#94335a" />
          </form>
        </Modal>
      )}

      {/* ── Cancel Confirm ── */}
      {cancelRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 420, width: '100%', margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 30, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
            <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#2b1b22', textAlign: 'center' }}>Cancel this visit?</p>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#8b7681', textAlign: 'center' }}>The nurse will be notified and the slot freed.</p>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Reason (optional)</label>
              <input style={inp} value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Patient unavailable, emergency, etc." />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setCancelRow(null)} disabled={cancelLoading} style={{ padding: '9px 22px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Keep Visit</button>
              <button onClick={handleCancel} disabled={cancelLoading} style={{ padding: '9px 22px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: cancelLoading ? 0.7 : 1 }}>
                {cancelLoading ? 'Cancelling…' : 'Cancel Visit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary = bg => ({ padding: '9px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const btnSm = bg => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' });
const ErrBox = ({ msg }) => <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{msg}</div>;
const Grid2 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>;
const Grid3 = ({ children, style }) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, ...style }}>{children}</div>;
const F = ({ label, k, type = 'text', form, set, required }) => (
  <div>
    <label style={lbl}>{label}</label>
    <input style={inp} type={type} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))} required={required} />
  </div>
);
const FmtActions = ({ saving, onCancel, label, color = '#94335a' }) => (
  <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
    <button type="submit" disabled={saving} style={{ padding: '9px 22px', background: color, color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : label}</button>
    <button type="button" onClick={onCancel} style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
  </div>
);
const Pagination = ({ page, total, per, onPage }) => {
  const pages = Math.ceil(total / per); if (pages <= 1) return null;
  return <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
    <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pgBtn}>‹</button>
    <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Page {page}/{pages}</span>
    <button onClick={() => onPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={pgBtn}>›</button>
  </div>;
};
const pgBtn = { padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
